/**
 * Per-step hint surfacing the best hour-of-day to land a journey step.
 * Used inside EngagementSequenceBuilder where each step has a delay_hours
 * (relative to journey start) but no absolute time. The hint tells the
 * builder what hour their delay should resolve to.
 */

import React from 'react';
import { Sparkles } from 'lucide-react';
import { useSendTimingRecs } from '@/hooks/ml';

interface Props {
  /** Language to scope recommendations. */
  language?: string | null;
  className?: string;
}

function formatHour(h: number): string {
  if (h === 0) return '12 AM';
  if (h === 12) return '12 PM';
  return h < 12 ? `${h} AM` : `${h - 12} PM`;
}

export const StepOptimalTimingHint: React.FC<Props> = ({ language, className = '' }) => {
  const { data, isLoading, isError, error } = useSendTimingRecs({
    language: language ?? undefined,
    topN: 5,
    enabled: true,
  });

  if (isError && error instanceof Error && error.message === 'model_not_trained') {
    return (
      <div className={`inline-flex items-center gap-1 text-xs text-amber-700 ${className}`}>
        <Sparkles className="h-3 w-3" /> Send-timing model not yet deployed.
      </div>
    );
  }
  if (isLoading) {
    return (
      <div className={`inline-flex items-center gap-1 text-xs text-gray-400 ${className}`}>
        <Sparkles className="h-3 w-3 animate-pulse" /> Loading best hour…
      </div>
    );
  }
  if (!data?.cells?.length) return null;

  // Aggregate by hour-of-day across the top cells (ignore day-of-week for
  // a journey step that fires N hours after the previous one).
  const hourScores = new Map<number, { rateSum: number; sendsSum: number; n: number }>();
  for (const c of data.cells) {
    const prev = hourScores.get(c.send_hour) ?? { rateSum: 0, sendsSum: 0, n: 0 };
    hourScores.set(c.send_hour, {
      rateSum: prev.rateSum + c.reply_rate,
      sendsSum: prev.sendsSum + c.sends,
      n: prev.n + 1,
    });
  }
  let bestHour = -1;
  let bestRate = 0;
  for (const [hour, { rateSum, n }] of hourScores) {
    const avg = rateSum / n;
    if (avg > bestRate) {
      bestRate = avg;
      bestHour = hour;
    }
  }
  if (bestHour < 0) return null;

  return (
    <div
      className={`inline-flex items-center gap-1 text-xs text-emerald-700 ${className}`}
      title={`Hour with highest average reply rate across the model's top recommendation cells${language ? ` (${language})` : ''}`}
    >
      <Sparkles className="h-3 w-3" />
      Best hour: <span className="font-medium">{formatHour(bestHour)}</span>
      <span className="text-emerald-600/80">({(bestRate * 100).toFixed(1)}% reply)</span>
    </div>
  );
};
